import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, FileText, Crown, Play, HelpCircle, User, AlertCircle } from 'lucide-react';
import { checkExamPermission, PermissionCheckResult } from '../utils/permissionCheck';
import { supabase } from '../supabase/client';
import { categoryStore } from '../store/categoryStore';
import PackagePurchaseModal from '../components/PackagePurchaseModal';

interface Paper {
  id: string;
  title: string;
  organization: string;
  category_type: string;
  level_text: string;
  question_count: number;
  total_score: number;
  nature: string;
  status: string;
  year?: number;
  month?: number;
  paper_type?: string;
  source?: string;
  is_free?: boolean;
  duration?: number;
}

const orgMapping: Record<string, string> = {
  'cysf': '中国计算机学会',
  'dzxh': '中国电子学会',
  'xxsy': '青少年信息素养大赛'
};

export default function ExamListPage() {
  const { categoryId, courseType, levelId } = useParams();
  const navigate = useNavigate();
  const [papers, setPapers] = useState<Paper[]>([]);
  const [permissions, setPermissions] = useState<Record<string, PermissionCheckResult>>({});
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setCurrentUser({ id: session.user.id });
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();
        setUserRole(profile?.role || '');
      }
    });
    fetchPapers();
  }, [categoryId, courseType, levelId]);

  useEffect(() => {
    if (papers.length > 0 && currentUser) {
      checkPermissions();
    } else {
      setCheckingPermissions(false);
    }
  }, [papers, currentUser]);

  const checkPermissions = async () => {
    setCheckingPermissions(true);
    const results: Record<string, PermissionCheckResult> = {};
    for (const paper of papers) {
      if (currentUser) {
        const result = await checkExamPermission(currentUser.id, paper.organization, paper.category_type || '', paper.level_text || '');
        results[paper.id] = result;
      }
    }
    setPermissions(results);
    setCheckingPermissions(false);
  };

  const fetchPapers = async () => {
    const orgValue = orgMapping[categoryId || ''] || categoryId;
    const decodedCourseType = decodeURIComponent(courseType || '');
    const levelNum = levelId || '';

    let query = supabase
      .from('papers')
      .select('*')
      .eq('is_published', true)
      .eq('status', 'published');

    if (orgValue) {
      query = query.eq('organization', orgValue);
    }
    if (decodedCourseType) {
      const mappedType = categoryStore.getCategoryNameByCode(decodedCourseType) || decodedCourseType;
      query = query.in('category_type', [decodedCourseType, mappedType]);
    }
    if (levelNum) {
      query = query.eq('level_text', decodeURIComponent(levelNum));
    }

    const { data, error } = await query;
    if (error) {
      
      return;
    }
    setPapers((data as Paper[]) || []);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">
            {orgMapping[categoryId || ''] || '试卷列表'}
            {courseType ? ` | ${decodeURIComponent(courseType)}` : ''}
            {levelId ? ` | ${levelId}` : ''}
          </h1>
          <p className="text-gray-500">共 {papers.length} 套试卷</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">试卷名称</th>
                <th className="px-4 py-3 text-center">答题时长</th>
                <th className="px-4 py-3 text-center">题数</th>
                <th className="px-4 py-3 text-center">级别分类</th>
                <th className="px-4 py-3 text-center">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {papers.map((paper) => (
                <tr key={paper.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{paper.title}</span>
                      {paper.is_free ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">免费</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded flex items-center gap-1">
                          <Crown className="w-3 h-3" />付费
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">{paper.duration || 60}分钟</td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">{paper.question_count || 0}题</td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">{paper.level_text || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    {checkingPermissions ? (
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    ) : paper.is_free ? (
                      <button
                        onClick={() => navigate(`/quiz/${paper.id}`)}
                        className="px-4 py-1.5 rounded text-sm flex items-center justify-center gap-1 transition-colors bg-blue-600 text-white hover:bg-blue-700"
                      >
                        <Play className="w-3 h-3" />开始答题
                      </button>
                    ) : permissions[paper.id]?.hasPermission ? (
                      <button
                        onClick={() => navigate(`/quiz/${paper.id}`)}
                        className="px-4 py-1.5 rounded text-sm flex items-center justify-center gap-1 transition-colors bg-blue-600 text-white hover:bg-blue-700"
                      >
                        <Play className="w-3 h-3" />开始答题
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedPaper(paper);
                          setShowPurchaseModal(true);
                        }}
                        className="px-4 py-1.5 rounded text-sm flex items-center justify-center gap-1 transition-colors bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-sm"
                      >
                        <Crown className="w-3 h-3" />付费解锁
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {papers.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>暂无试卷</p>
            </div>
          )}
        </div>
      </div>

      {showPurchaseModal && selectedPaper && (
        <PackagePurchaseModal
          paper={selectedPaper}
          onClose={() => {
            setShowPurchaseModal(false);
            setSelectedPaper(null);
          }}
          role={userRole === 'teacher' ? 'teacher' : 'student'}
        />
      )}
    </div>
  );
}
